import { Box, Menu, Text } from 'grommet';
import { More } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { CostTier, FieldTier } from '../../CardTiers';
import { ConfirmModal } from '../../ConfirmModal';
import { LabeledField } from '../../LabeledField';
import { DeleteModal } from '../../DeleteModal';
import { EditableText } from '../../EditableText';
import { ContractSelect } from '../ContractSelect';
import { UsageEstimate } from '@talon/web/lib/api';
import { useEstimateStore } from '@talon/web/src/context';
import { recalcKey } from '@talon/web/src/context/stores/estimateStore';
import { USD } from '@talon/web/src/utils/currency';
import { isItemFieldEdited, itemDefaultFields } from '@shared/utils';

export interface IUsageEstimateHeaderProps {
  usageEstimate: UsageEstimate;
  setUsageEstimate: (usageEstimate: UsageEstimate) => void;
}

export const UsageEstimateHeader = observer(({ usageEstimate, setUsageEstimate }: IUsageEstimateHeaderProps) => {
  /** Context **/
  const estimateStore = useEstimateStore();

  /** State **/
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [title, setTitle] = useState(usageEstimate.title);
  const [showResetModal, setShowResetModal] = useState(false);

  /** Computed **/
  const canEdit = estimateStore.canEditCurrent;
  const totalsBusy = estimateStore.isRecalculating(
    recalcKey.table('usage', usageEstimate.id),
    recalcKey.rows('usage', usageEstimate.id)
  );
  const hasEdits = !!usageEstimate.items?.some((item) =>
    itemDefaultFields.some((field) => isItemFieldEdited(item, field))
  );

  /** Methods **/
  const deleteUsageEstimate = async () => {
    await estimateStore.deleteUsageEstimate(usageEstimate.id);
  };

  /** Render **/
  const menuItems = [{ label: 'Delete', onClick: () => setShowDeleteModal(true) }];
  if (hasEdits) menuItems.unshift({ label: 'Reset edits', onClick: () => setShowResetModal(true) });

  return (
    <Box>
      {/* Tier 1: the usage estimate's settings */}
      <FieldTier>
        <LabeledField label="Usage estimate" width={{ min: '14em' }} flex>
          {canEdit ? (
            <EditableText
              val={title}
              onChange={setTitle}
              // The title just typed: saved straight away (Enter), its state update hasn't happened yet
              onSubmit={(typed?: string) => setUsageEstimate({ ...usageEstimate, title: typed ?? title })}
              onBlur={(typed?: string) => setUsageEstimate({ ...usageEstimate, title: typed ?? title })}
            />
          ) : (
            <Text>{title}</Text>
          )}
        </LabeledField>
        <LabeledField label="Contract">
          <ContractSelect
            hideLabel
            automaticBy="shoot date"
            appliedId={usageEstimate.rate_schedule_id}
            overrideId={usageEstimate.rate_schedule_override_id}
            onChange={(rateScheduleId) => estimateStore.setUsageEstimateContract(usageEstimate.id, rateScheduleId)}
          />
        </LabeledField>
        <Box flex />
        {canEdit && (
          <Menu
            icon={<More color="text-meta" />}
            a11yTitle={`More actions for ${title}`}
            dropAlign={{ top: 'bottom', right: 'right' }}
            dropProps={{ background: 'white', pad: 'xsmall', round: '8px', elevation: 'medium' }}
            items={menuItems}
          />
        )}
      </FieldTier>

      {/* Tier 2: what it costs */}
      <CostTier
        summary={hasEdits ? 'Some rates or fringe were edited by hand' : undefined}
        busy={totalsBusy}
        costs={[
          { label: 'Subtotal', value: USD(usageEstimate.subtotal) },
          { label: 'Subtotal + fringe', value: USD(usageEstimate.subtotal_plus_fringe), emphasis: true },
        ]}
      />
      {showResetModal && (
        <ConfirmModal
          content="Set every rate and fringe edited by hand in this table back to the contract’s?"
          confirmLabel="Reset edits"
          onConfirm={() => {
            setShowResetModal(false);
            estimateStore.resetUsageEstimateEdits(usageEstimate.id);
          }}
          onCancel={() => setShowResetModal(false)}
        />
      )}
      {showDeleteModal && usageEstimate && (
        <DeleteModal
          name={usageEstimate.title}
          onDelete={deleteUsageEstimate}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </Box>
  );
});
